import React, { useCallback, useMemo, useRef } from "react";
import _ from "lodash";
import {
  Button,
  ClickAwayListener,
  Divider,
  Grow,
  Link,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 0,
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1)
  },
  link: {
    width: "100%",
    padding: "6px 16px"
  },
  listItem: {
    padding: 0
  },
  section: {
    flexGrow: 0,
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    "& :not(:last-child)": {
      marginRight: theme.spacing(2)
    }
  },
  button: {
    marginRight: theme.spacing(2),
    fontSize: theme.typography.body1.fontSize,
    fontWeight: "normal",
    "& .MuiButton-label": {
      marginRight: 0,
      textTransform: "none"
    }
  },
  popper: {
    zIndex: theme.zIndex.speedDial
  }
}));

const OGNMenu = ({ anchorRef, siteMenu = [] }) => {
  const classes = useStyles();
  const selfRef = useRef(null);

  const [open, setOpen] = React.useState(false);
  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const refToUse = anchorRef ?? selfRef;

  const handleClose = useCallback(
    event => {
      if (refToUse?.current && refToUse?.current.contains(event.target)) {
        return;
      }

      setOpen(false);
    },
    [refToUse]
  );

  const handleListKeyDown = event => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      // eslint-disable-next-line no-unused-expressions
      refToUse?.current.focus();
    }

    prevOpen.current = open;
  }, [open, refToUse]);

  const OGNMenuItems = useMemo(
    () =>
      _.map(siteMenu, (menuItem, index) => {
        const [label, href] = menuItem;
        return "-" === label ? (
          <Divider key={index} />
        ) : (
          <MenuItem
            key={index}
            className={classes.listItem}
            onClick={handleClose}
            onKeyDown={event => {
              if (event.key === "Enter") {
                event.preventDefault();
                setOpen(false);
                window.open(href);
              }
            }}
          >
            <Link
              className={classes.link}
              target="_blank"
              href={href}
              underline="none"
              rel="noopener noreferrer"
              color="textPrimary"
              variant="body1"
            >
              {label}
            </Link>
          </MenuItem>
        );
      }),
    [handleClose, siteMenu, classes.link, classes.listItem]
  );

  return (
    <>
      <Button
        ref={selfRef}
        className={classes.button}
        color="inherit"
        endIcon={<ArrowDropDownIcon />}
        onClick={handleToggle}
      >
        OGN
      </Button>
      <Popper
        className={classes.popper}
        open={open}
        anchorEl={refToUse?.current}
        role="menu"
        transition
        disablePortal
        placement="bottom-start"
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: "center top"
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="OGN-menu-list"
                  onKeyDown={handleListKeyDown}
                >
                  {OGNMenuItems}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default OGNMenu;
